import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { inquiryAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import {
  Loader2,
  MessageSquare,
  Calendar,
  Users,
  Mail,
  Phone,
  Clock,
  Send,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const AdminInquiries = () => {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sendingId, setSendingId] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await inquiryAPI.getAll(token);
      setInquiries(response.data);
    } catch (error) {
      toast.error("Failed to fetch inquiries");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await inquiryAPI.updateStatus(id, status, token);
      toast.success("Status updated");
      fetchInquiries();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSendToOwner = async (inquiryId) => {
    setSendingId(inquiryId);
    try {
      const response = await axios.post(
        `${API_URL}/admin/inquiries/${inquiryId}/send-to-owner`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchInquiries();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to send enquiry";
      toast.error(detail);
    } finally {
      setSendingId(null);
    }
  };

  const filteredInquiries = inquiries.filter((inquiry) => {
    if (filter === "all") return true;
    return inquiry.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700";
      case "contacted":
        return "bg-blue-100 text-blue-700";
      case "confirmed":
        return "bg-green-100 text-green-700";
      case "cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const getForwardedBadge = (inquiry) => {
    if (!inquiry.forwarded_status) return null;
    switch (inquiry.forwarded_status) {
      case "sent":
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
            <CheckCircle className="w-3 h-3" /> Sent via {inquiry.forwarded_via}
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
            <XCircle className="w-3 h-3" /> Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
            <AlertTriangle className="w-3 h-3" /> {inquiry.forwarded_status}
          </span>
        );
    }
  };

  const getWhatsAppUrl = (inquiry) => {
    const msg = [
      `*New Booking Enquiry - ${inquiry.homestay_name}*`,
      ``,
      `Guest: ${inquiry.name}`,
      `Email: ${inquiry.email}`,
      `Phone: ${inquiry.phone}`,
      inquiry.check_in_date ? `Check-in: ${inquiry.check_in_date}` : null,
      inquiry.check_out_date ? `Check-out: ${inquiry.check_out_date}` : null,
      inquiry.guests ? `Guests: ${inquiry.guests}` : null,
      ``,
      `Message: ${inquiry.message || "No message"}`,
      ``,
      `— Kokan Homestays`,
    ]
      .filter(Boolean)
      .join("\n");
    return `https://wa.me/${inquiry._owner_whatsapp}?text=${encodeURIComponent(msg)}`;
  };

  const pendingCount = inquiries.filter((i) => i.status === "pending").length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-inquiries">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">
              Inquiries
            </h1>
            <p className="text-muted-foreground">
              Manage booking inquiries from guests
            </p>
          </div>

          {/* Filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="inquiry-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({inquiries.length})</SelectItem>
              <SelectItem value="pending">Pending ({pendingCount})</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Inquiries List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredInquiries.length > 0 ? (
          <div className="space-y-4">
            {filteredInquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                className="bg-white rounded-2xl p-6 shadow-soft"
                data-testid={`inquiry-item-${inquiry.id}`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <h3 className="text-xl font-semibold text-foreground">
                        {inquiry.name}
                      </h3>
                      <Badge className={getStatusColor(inquiry.status)}>
                        {inquiry.status.charAt(0).toUpperCase() + inquiry.status.slice(1)}
                      </Badge>
                      {getForwardedBadge(inquiry)}
                    </div>

                    <p className="text-primary font-medium mb-4">
                      {inquiry.homestay_name}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${inquiry.email}`} className="hover:text-primary">
                          {inquiry.email}
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${inquiry.phone}`} className="hover:text-primary">
                          {inquiry.phone}
                        </a>
                      </div>
                      {inquiry.check_in_date && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {inquiry.check_in_date}
                            {inquiry.check_out_date && ` - ${inquiry.check_out_date}`}
                          </span>
                        </div>
                      )}
                      {inquiry.guests && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Users className="w-4 h-4" />
                          <span>{inquiry.guests} guests</span>
                        </div>
                      )}
                    </div>

                    <div className="bg-muted/50 rounded-lg p-4 mb-4">
                      <p className="text-muted-foreground">{inquiry.message}</p>
                    </div>

                    {/* Forwarding info */}
                    {inquiry.forwarded_at && (
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <Send className="w-3 h-3" />
                        <span>
                          Forwarded to {inquiry.forwarded_to} on{" "}
                          {new Date(inquiry.forwarded_at).toLocaleString()}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(inquiry.created_at).toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 min-w-[160px]">
                    {/* Send to Owner via Email */}
                    <Button
                      size="sm"
                      onClick={() => handleSendToOwner(inquiry.id)}
                      disabled={sendingId === inquiry.id}
                      className={`${
                        inquiry.forwarded_status === "sent"
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-blue-600 hover:bg-blue-700"
                      } text-white`}
                      data-testid={`send-to-owner-${inquiry.id}`}
                    >
                      {sendingId === inquiry.id ? (
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-1" />
                      )}
                      {inquiry.forwarded_status === "sent" ? "Resend Email" : "Send Email"}
                    </Button>

                    {/* Send to Owner via WhatsApp */}
                    {inquiry._owner_whatsapp && (
                      <Button
                        size="sm"
                        asChild
                        className="bg-[#25D366] hover:bg-[#1da851] text-white"
                        data-testid={`send-whatsapp-${inquiry.id}`}
                      >
                        <a
                          href={getWhatsAppUrl(inquiry)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                          WhatsApp
                        </a>
                      </Button>
                    )}

                    {/* Status Select */}
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">
                        Status
                      </label>
                      <Select
                        value={inquiry.status}
                        onValueChange={(value) => handleStatusChange(inquiry.id, value)}
                      >
                        <SelectTrigger data-testid={`status-select-${inquiry.id}`} className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Inquiries Found
            </h3>
            <p className="text-muted-foreground">
              {filter !== "all"
                ? "No inquiries match the selected filter."
                : "Booking inquiries will appear here once guests submit them."}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminInquiries;
